/**
 * wyvio-ui · Composant Button
 * ───────────────────────────
 * Variantes : primary (lime), secondary (surface), ghost (transparent), icon.
 * Tailles : sm (h-8), md (h-9), lg (h-10).
 * Interdiction : deux `primary` visibles simultanément sur une même surface.
 */
import { forwardRef } from "react";
import { Loader2 } from "lucide-react";

const V = {
  primary:
    "bg-[#C8FF2F] text-[#0A0A0A] hover:brightness-95 active:brightness-90 active:scale-[0.98]",
  secondary:
    "bg-white dark:bg-[#111111] text-[#0A0A0A] dark:text-white border border-[#EEF2F5] dark:border-[#262626] hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
  ghost:
    "text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white",
  dark:
    "bg-[#0A0A0A] dark:bg-white text-white dark:text-[#0A0A0A] hover:brightness-110",
};
const S = {
  sm: "h-8 px-3 text-[11.5px]",
  md: "h-9 px-4 text-[12.5px]",
  lg: "h-10 px-5 text-[13px]",
};

export const Button = forwardRef(function Button(
  {
    variant = "secondary",
    size = "md",
    icon: Icon,
    iconRight,
    loading = false,
    disabled = false,
    children,
    className = "",
    ...rest
  },
  ref,
) {
  const cls = [
    "inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap",
    "transition-all duration-200 ease-out",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8FF2F]",
    "disabled:opacity-50 disabled:pointer-events-none",
    V[variant] || V.secondary,
    S[size] || S.md,
    className,
  ].join(" ");
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cls}
      {...rest}
    >
      {loading ? (
        <Loader2 size={13} className="animate-spin" strokeWidth={2} />
      ) : Icon ? (
        <Icon size={13} strokeWidth={2.5} />
      ) : null}
      {children}
      {iconRight}
    </button>
  );
});

export const IconButton = forwardRef(function IconButton(
  { icon: Icon, size = 9, className = "", loading = false, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={[
        "rounded-full flex items-center justify-center",
        "bg-white dark:bg-[#111111]",
        "border border-[#EEF2F5] dark:border-[#262626]",
        "text-[#9CA3AF] dark:text-[#737373]",
        "hover:text-[#0A0A0A] dark:hover:text-white",
        "hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
        "transition-all duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8FF2F]",
        `h-${size} w-${size}`,
        className,
      ].join(" ")}
      {...rest}
    >
      {loading ? (
        <Loader2 size={13} className="animate-spin" strokeWidth={2} />
      ) : (
        <Icon size={13} strokeWidth={2} />
      )}
    </button>
  );
});
